import {DocumentNode, gql} from '@apollo/client';
import {entry, objectFromEntries} from '@benchling/js-utils';

import {assert} from 'Asserts';
import {ProcessTaskStatusType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {getAccessibleOrInaccessibleId, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {sortNodesByCoordinates} from 'procedures/design/flowchartManagementUtils';
import {ExperimentalInputs} from 'procedures/planning/ProcedurePlanTypes';
import {fillPlanMethodVersionWithPlannedValuesAndConfirmationMethods} from 'procedures/utils/fillPlanMethodVersionWithPlannedValuesAndConfirmationMethods';
import {getPowAgnosticTemplateFlowchartVersionFromProcedureRun} from 'procedures/utils/powAgnosticFlowchartHelpers';
import {
  EXPERIMENTAL_INPUTS_FLOWCHART_FRAGMENT,
  ExperimentFlowchart,
  ExperimentMethod,
  getValuesForMethod,
  prepareInitialExperimentalInputs,
  ProcedureMethodDefinitionVersionNodeParameters,
} from 'studies/typed/shared/ExperimentalInputs';
import {ConditionGroup} from 'studies/typed/shared/useExperimentDesignState';

import {GetStudyDesignFromProcedureRunGraphQL as ProcedureRunNode} from './__generated/GetStudyDesignFromProcedureRunGraphQL';

interface StudyDesignParams {
  experimentalInputs: ExperimentalInputs;
  templateFlowchart: ExperimentFlowchart;
  /** Condition groups loaded from the procedure run; by definition all persisted. */
  initialPersistedConditionGroups: Array<ConditionGroup>;
}

export interface MethodExecutionStatus {
  methodId: string;
  conditionId: string;
  replicateNumber: number;
  status: ProcessTaskStatusType | null;
}

/**
 * Produces one flattened status record per method execution instance (PMEI). `status` is the raw
 * task status, or `null` when the method task is missing or inaccessible.
 *
 * PMEIs with an inaccessible template method are dropped
 * so their cells render unstyled rather than crashing the design tab.
 */
export function getMethodExecutionStatuses(procedureRun: ProcedureRunNode): Array<MethodExecutionStatus> {
  return procedureRun.methodExecutionInstances
    .filter((pmei) => !isInaccessible(pmei.templateMethodVersion))
    .map((pmei) => {
      const replicate = pmei.procedureRunConditionReplicate;
      return {
        methodId: getAccessibleOrInaccessibleId(pmei.templateMethodVersion),
        conditionId: replicate.procedureRunCondition.id,
        replicateNumber: replicate.replicateNumber,
        status:
          pmei.methodTask == null || isInaccessible(pmei.methodTask)
            ? null
            : pmei.methodTask.status.statusType,
      };
    });
}

/**
 * Extracts study design state from a `ProcedureRunNode`: condition groups (built from
 * `procedureRun.conditions`), the template flowchart, and the per-group × per-method
 * `ExperimentalInputs` cell values reconstituted from each PMEI's planned values.
 *
 * @param procedureRun - a ProcedureRunNode that contains STUDY_DESIGN_FROM_PROCEDURE_RUN_FRAGMENT
 */
export default function getStudyDesignFromProcedureRun(procedureRun: ProcedureRunNode): StudyDesignParams {
  // This is the flowchart version that corresponds to the procedure the user selected to initiate the experiment.
  const flowchartVersion = getPowAgnosticTemplateFlowchartVersionFromProcedureRun(procedureRun);
  assert(!isInaccessible(flowchartVersion), 'Expected flowchart version to be accessible');
  const nodes = flowchartVersion.nodes;
  // The <ProcedureRunPlan> should thus render the ordered methods of this flowchart.
  const templateMethods: Array<ExperimentMethod> = sortNodesByCoordinates(
    [...nodes],
    'gridLayoutCoordinates'
  );

  const templateFlowchart = {...flowchartVersion, nodes: templateMethods};

  const templateMethodVersionIds = templateMethods.map((m) => m.id);
  const initialPersistedConditionGroups: Array<ConditionGroup> = buildConditionGroupsFromBackendConditions(
    procedureRun.conditions,
    templateMethodVersionIds
  );
  // Bowling-lane invariant: every condition group covers every flowchart method.
  for (const group of initialPersistedConditionGroups) {
    const missing = templateMethodVersionIds.filter((id) => group.conditionIdsByPmdvId[id] == null);
    assert(
      missing.length === 0,
      `Condition group "${group.name}" is missing entries for method versions [${missing.join(', ')}]`
    );
  }

  // Invariant: a backend `ProcedureRunCondition.id` belongs to exactly one condition group.
  const conditionGroupIndexByConditionId = new Map<string, number>();
  initialPersistedConditionGroups.forEach((group, groupIndex) => {
    for (const conditionId of Object.values(group.conditionIdsByPmdvId)) {
      conditionGroupIndexByConditionId.set(conditionId, groupIndex);
    }
  });

  // Bucket each PMEI's planned values by (templateMethodVersionId, conditionGroupIndex) so
  // the reconstituted ExperimentalInputs values fall into the right group × method.
  const planMethodByTemplateMethodAndConditionGroup: {
    [methodId: string]: {[conditionGroupIndex: number]: ProcedureMethodDefinitionVersionNodeParameters};
  } = {};
  procedureRun.methodExecutionInstances.forEach((pmei) => {
    const templateMethodVersionId = getAccessibleOrInaccessibleId(pmei.templateMethodVersion);
    const conditionGroupIndex = conditionGroupIndexByConditionId.get(
      pmei.procedureRunConditionReplicate.procedureRunCondition.id
    );
    assert(
      conditionGroupIndex != null,
      `PMEI references condition "${pmei.procedureRunConditionReplicate.procedureRunCondition.id}" not in any group`
    );
    const method = pmei.planMethodDefinitionVersion;
    const parameterConfirmationMethodsByParameterId = new Map(
      pmei.parameterConfirmationMethods.map(({parameterId, confirmationMethod}) => [
        parameterId,
        confirmationMethod,
      ])
    );
    planMethodByTemplateMethodAndConditionGroup[templateMethodVersionId] ??= {};
    planMethodByTemplateMethodAndConditionGroup[templateMethodVersionId][conditionGroupIndex] =
      fillPlanMethodVersionWithPlannedValuesAndConfirmationMethods(
        method,
        pmei.dataParameterPlannedValues,
        pmei.materialInputPlannedValues,
        pmei.equipmentPlannedValues,
        parameterConfirmationMethodsByParameterId,
        // For process intermediates, we do want to include the fallback template info
        {fallbackToPlanMaterialInputs: true}
      );
  });
  // Reconstitute per-method cell values from the bucketed PMEIs. The condition-group
  // builder guarantees every (method, conditionGroup) cell is covered, so a missing entry
  // here is a real bug — crash hard rather than fall back to template defaults silently.
  const planValues = objectFromEntries(
    templateMethods.map((methodNode) =>
      entry(methodNode.id, {
        values: initialPersistedConditionGroups.map((_, conditionGroupIndex) =>
          getValuesForMethod(planMethodByTemplateMethodAndConditionGroup[methodNode.id][conditionGroupIndex])
        ),
      })
    )
  );
  // Parameter confirmation methods might have changed in planning, so extract those too.
  // We already filled them onto the plan method above.
  const prevConfirmationMethods = objectFromEntries(
    templateMethods.map((methodNode) =>
      entry(methodNode.id, {
        parameterConfirmationMethods: getPlannedParameterConfirmationMethods(
          methodNode,
          planMethodByTemplateMethodAndConditionGroup
        ),
      })
    )
  );
  // Finally, we can construct the full ExperimentalInputs data structure, seeded with the planValues and
  // plan confirmation methods
  const experimentalInputs = prepareInitialExperimentalInputs(
    initialPersistedConditionGroups.length,
    templateMethods,
    {prevExperimentalInputValues: planValues, prevConfirmationMethods}
  );

  return {experimentalInputs, templateFlowchart, initialPersistedConditionGroups};
}

function getPlannedParameterConfirmationMethods(
  templateMethod: ExperimentMethod,
  planMethodByTemplateMethodAndConditionGroup: {
    [methodId: string]: {[conditionGroupIndex: number]: ProcedureMethodDefinitionVersionNodeParameters};
  }
): ExperimentalInputs[string]['parameterConfirmationMethods'] {
  const planMethods = Object.values(planMethodByTemplateMethodAndConditionGroup[templateMethod.id] ?? {});

  // We expect that all plan methods have the same parameter confirmation methods, even if they may differ
  // from the template method.
  assert(planMethods.length >= 1, 'Expected at least 1 matching planned method');
  const referencePlanMethod = planMethods[0];
  planMethods.forEach((planMethod) => {
    assert(
      planMethod.dataParameterSettings.length === templateMethod.dataParameterSettings.length,
      'Expected planned method to have same number of data parameters as template method'
    );
    planMethod.dataParameterSettings.forEach(({version: {name, confirmationMethod}}, i) => {
      assert(
        name === templateMethod.dataParameterSettings[i].version.name,
        'Expected planned method to have same order of data parameters as template method'
      );
      assert(
        confirmationMethod === referencePlanMethod.dataParameterSettings[i].version.confirmationMethod,
        'Expected all planned methods to have same parameter confirmation methods'
      );
    });
    assert(
      planMethod.materialInputSettings.length === templateMethod.materialInputSettings.length,
      'Expected planned method to have same number of material inputs as template method'
    );
    planMethod.materialInputSettings.forEach(({version: {name, confirmationMethod}}, i) => {
      assert(
        name === templateMethod.materialInputSettings[i].version.name,
        'Expected planned method to have same order of material inputs as template method'
      );
      assert(
        confirmationMethod === referencePlanMethod.materialInputSettings[i].version.confirmationMethod,
        'Expected all planned methods to have same material confirmation methods'
      );
    });
    assert(
      planMethod.equipmentSettings.length === templateMethod.equipmentSettings.length,
      'Expected planned method to have same number of equipment as template method'
    );
    planMethod.equipmentSettings.forEach(({version: {name, confirmationMethod}}, i) => {
      assert(
        name === templateMethod.equipmentSettings[i].version.name,
        'Expected planned method to have same order of equipment as template method'
      );
      assert(
        confirmationMethod === referencePlanMethod.equipmentSettings[i].version.confirmationMethod,
        'Expected all planned methods to have same equipment confirmation methods'
      );
    });
  });

  // Extract the planned confirmation methods from the planned methods
  return {
    parameters: templateMethod.dataParameterSettings.map(
      (_, i) => referencePlanMethod.dataParameterSettings[i].version.confirmationMethod
    ),
    materialInputs: templateMethod.materialInputSettings.map(
      (_, i) => referencePlanMethod.materialInputSettings[i].version.confirmationMethod
    ),
    equipment: templateMethod.equipmentSettings.map(
      (_, i) => referencePlanMethod.equipmentSettings[i].version.confirmationMethod
    ),
  };
}

/**
 * Converts a `ProcedureRun`'s `conditions` list into {@link ConditionGroup}s, handling a mix
 * of shared (`templateMethodVersion: null`) and per-method conditions.
 *
 * Grouping strategy: walk the backend conditions in order. A shared condition becomes a group
 * on its own, with its id replicated across every entry of `conditionIdsByPmdvId` (one per
 * flowchart method). A per-method condition is folded into an existing group keyed by
 * `(name, description, numReplicates)` if one exists, populating only its owning method's
 * entry; otherwise it starts a new group. Groups preserve first-encounter order.
 *
 * Throws on a duplicate per-method entry for the same method within a single group — a
 * backend data integrity violation.
 */
export function buildConditionGroupsFromBackendConditions(
  backendConditions: ReadonlyArray<ProcedureRunNode['conditions'][number]>,
  templateMethodVersionIds: ReadonlyArray<string>
): Array<ConditionGroup> {
  const groups: Array<ConditionGroup> = [];
  const perMethodGroupIndexByKey = new Map<string, number>();

  for (const condition of backendConditions) {
    const numReplicates = condition.procedureRunConditionReplicates.length;

    if (condition.templateMethodVersion == null) {
      // TODO(BNCH-154831): drop this branch after the per-method backfill — `templateMethodVersion`
      // is never null in the new world.
      const conditionIdsByPmdvId: Record<string, string> = {};
      for (const methodId of templateMethodVersionIds) {
        conditionIdsByPmdvId[methodId] = condition.id;
      }
      groups.push({
        name: condition.name,
        description: condition.description,
        numReplicates,
        conditionIdsByPmdvId,
      });
      continue;
    }

    const methodVersionId = getAccessibleOrInaccessibleId(condition.templateMethodVersion);
    const groupKey = JSON.stringify([condition.name, condition.description, numReplicates]);
    const existingIndex = perMethodGroupIndexByKey.get(groupKey);
    if (existingIndex == null) {
      perMethodGroupIndexByKey.set(groupKey, groups.length);
      groups.push({
        name: condition.name,
        description: condition.description,
        numReplicates,
        conditionIdsByPmdvId: {[methodVersionId]: condition.id},
      });
    } else {
      const group = groups[existingIndex];
      assert(
        group.conditionIdsByPmdvId[methodVersionId] == null,
        `Duplicate per-method condition for method "${methodVersionId}" in condition group "${condition.name}"`
      );
      group.conditionIdsByPmdvId[methodVersionId] = condition.id;
    }
  }

  return groups;
}

export const STUDY_DESIGN_FROM_PROCEDURE_RUN_FRAGMENT: DocumentNode = gql`
  fragment GetStudyDesignFromProcedureRunGraphQL on ProcedureRunNode {
    id
    name
    templateProcedureFlowchartVersion {
      ... on ProcedureFlowchartVersionNode {
        ...ExperimentalInputsFlowchartGraphQL
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
    procedureFlowchartConfigVersionProxy {
      ...ProcedureFlowchartConfigVersionProxyAllFieldsGraphQL
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
    methodExecutionInstances {
      ...ProcedureMethodExecutionInstancePlannedValuesGraphQL
      planMethodDefinitionVersion {
        ...BaseProcedureMethodDefinitionVersionGraphQL
        materialInputSettings {
          templateValues {
            id
            material
            amount
          }
        }
      }
      parameterConfirmationMethods {
        parameterId
        confirmationMethod
      }
      templateMethodVersion {
        ... on ProcedureMethodDefinitionVersionNode {
          id
        }
        ... on InaccessibleInfoNode {
          ...InaccessibleInfoGraphQL
        }
      }
      methodTask {
        ... on ProcessTaskNode {
          id
          status {
            ... on ProcessTaskStatusNode {
              id
              statusType
            }
          }
        }
        ... on InaccessibleInfoNode {
          ...InaccessibleInfoGraphQL
        }
      }
      procedureRunConditionReplicate {
        id
        replicateNumber
        procedureRunCondition {
          id
        }
      }
    }
    conditions {
      id
      name
      description
      templateMethodVersion {
        ... on ProcedureMethodDefinitionVersionNode {
          id
        }
        ... on InaccessibleInfoNode {
          ...InaccessibleInfoGraphQL
        }
      }
      procedureRunConditionReplicates {
        id
      }
    }
  }
  ${getFragmentWrapper('ProcedureMethodExecutionInstancePlannedValues').fragment}
  ${getFragmentWrapper('BaseProcedureMethodDefinitionVersion').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${getFragmentWrapper('ProcedureFlowchartConfigVersionProxyAllFields').fragment}
  ${EXPERIMENTAL_INPUTS_FLOWCHART_FRAGMENT}
`;
